import * as React from "react"
import { HostingCertificate } from "@modules/graphql/types"
import { Heading, ThemeCss, Button } from "gatsby-interface"
import { format } from "date-fns"
import { gatsbyCloudCdnIntegrationConfig as text } from "@modules/locales/default.json"
import { MdLockOutline } from "react-icons/md"
import { lockIcon } from "../styles/gatsbyCloudCdn"
import { stringifyCertDomains } from "../../shared/utils/stringifyCertDomains"
import { formatCertSerial } from "../../shared/utils/formatCertSerial"

const rowCss: ThemeCss = _theme => ({
  alignItems: `center`,
  display: `flex`,
  justifyContent: `space-between`,
})

const headingCss: ThemeCss = theme => ({
  fontFamily: theme.fonts.body,
  fontWeight: theme.fontWeights.semiBold,
  paddingLeft: theme.space[8],
  fontSize: theme.fontSizes[2],
  position: `relative`,
})

const managedCss: ThemeCss = theme => ({
  paddingLeft: theme.space[8],
  marginTop: theme.space[4],
})

const detailsCss: ThemeCss = theme => ({
  display: `grid`,
  fontSize: theme.fontSizes[1],
  gridTemplateColumns: `max-content 1fr`,
  gridColumnGap: theme.space[6],
  gridRowGap: theme.space[2],
  paddingLeft: theme.space[8],
  color: theme.colors.grey[90],
  marginTop: theme.space[4],

  dt: {
    fontWeight: theme.fontWeights.body,
  },
  dd: {
    margin: 0,
    fontWeight: theme.fontWeights.semiBold,
  },
})

export type SslCertificateDetailsProps = {
  certificate: HostingCertificate
  onRemove?: () => void
}
export function SslCertificateDetails({
  certificate,
  onRemove,
  ...rest
}: SslCertificateDetailsProps) {
  const {
    issuer,
    domains,
    createdAt,
    notAfter,
    managed,
    canDelete,
    serialNumber,
  } = certificate
  const certDomains = stringifyCertDomains(domains)

  return (
    <React.Fragment>
      <div css={rowCss}>
        <Heading css={headingCss} as="h5">
          <MdLockOutline css={lockIcon} /> {issuer}
        </Heading>
        {canDelete && (
          <Button onClick={onRemove} variant="GHOST" size="S">
            {text.actions.remove}
          </Button>
        )}
      </div>
      <Heading css={managedCss} as="h6">
        {managed ? text.labels.managed : text.labels.custom}
      </Heading>
      <dl css={detailsCss} {...rest}>
        <dt>{text.labels.domains}</dt>
        <dd>{certDomains}</dd>
        <dt>{text.labels.created}</dt>
        <dd>{format(new Date(createdAt), `MMM d, Y `)}</dd>
        <dt>{text.labels.expires}</dt>
        <dd>{format(new Date(notAfter), `MMM d, Y`)}</dd>
        <dt>{text.labels.serialNumber}</dt>
        <dd>{formatCertSerial(serialNumber)}</dd>
      </dl>
    </React.Fragment>
  )
}

const detailCss: ThemeCss = theme => ({
  fontWeight: theme.fontWeights.body,
  fontSize: theme.fontSizes[0],
  color: theme.colors.grey[60],
})

export type SslCertificateIsGeneratingProps = {
  domain: string
}
export function SslCertificateIsGenerating({
  domain,
}: SslCertificateIsGeneratingProps) {
  return (
    <Heading css={headingCss} as="h5">
      <MdLockOutline css={lockIcon} /> {text.messages.generatingSslCertificate}{" "}
      <span css={detailCss}>{`(${domain})`}</span>
    </Heading>
  )
}
